import React, { useEffect, useState } from "react";
import { TextField, Button, Typography, Box, Alert } from "@mui/material";
import { useUserContext } from "../../context/UserContext";
import {
  getUserById,
  registerUser,
} from "../../client/api"; // API call for signup
import LogoContainer from "../../components/LogoContainer";
import Logo from "../../components/Logo";
import useStyles from "./styles"; // Import the styles
import { useNavigate, useSearchParams } from "react-router-dom";
import Cookies from "js-cookie";
import { getSafeRedirectTarget } from "../../utils/ssoCookie";

const SPIKEUP_EMAIL_DOMAIN = "@spikeup.com";
const isSpikeUpEmail = (value: string) => value.toLowerCase().endsWith(SPIKEUP_EMAIL_DOMAIN);

const Signup: React.FC = () => {
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  // A cross-app invite (farm-manager/zapflow "invite a teammate") lands
  // here via ?email=...&redirect=<app frontend URL> - see
  // appAccessGrantService.sendInviteEmailForGrant on the backend. Email is
  // locked in that case so the account created actually matches the one
  // the invite/grant was issued to; redirect is carried through signup ->
  // verify-email-pending -> the verification email's own link -> /login,
  // which already knows how to bounce into the right app from there.
  const invitedEmail = searchParams.get("email");
  const redirectParam = getSafeRedirectTarget(searchParams.get("redirect"));
  // Only present when the inviter isn't a SpikeUp employee themselves (see
  // appAccessGrantService.createOrFulfillGrant) - the invitee joins the
  // same organization as whoever invited them, so this is pre-filled+locked
  // the same way email is, rather than asking them to track down their own
  // org's key.
  const invitedOrgKey = searchParams.get("organizationKey");

  const [name, setName] = useState("");
  const [email, setEmail] = useState(invitedEmail || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState(""); // New state for password confirmation
  const [accessKey, setAccessKey] = useState("");
  const [organizationKey, setOrganizationKey] = useState(invitedOrgKey || ""); // New state for organization key
  const [passwordError, setPasswordError] = useState(""); // State for password error message
  const [signupError, setSignupError] = useState("");
  const { user, setUser, setCurrentWorkspace } = useUserContext();
  const navigate = useNavigate(); // Initialize useNavigate

  const showOrganizationKeyField = !isSpikeUpEmail(email);

  const handleSignup = async () => {
    if (password !== confirmPassword) {
      setPasswordError("Passwords do not match."); // Set error message
      return; // Prevent signup
    }

    setPasswordError(""); // Clear any previous error messages
    setSignupError("");

    try {
      // SpikeUp emails don't need an organization key - they get verified
      // by email instead and are auto-assigned to the SpikeUp organization.
      await registerUser(
        name,
        email,
        password,
        "user",
        showOrganizationKeyField ? organizationKey : "",
        redirectParam
      );

      // Every new account is unverified until the emailed link is clicked -
      // no login/cookie/access-check happens here anymore, that all happens
      // at actual login time once verified.
      const pendingUrl = redirectParam
        ? `/verify-email-pending?email=${encodeURIComponent(email)}&redirect=${encodeURIComponent(redirectParam)}`
        : `/verify-email-pending?email=${encodeURIComponent(email)}`;
      navigate(pendingUrl);
    } catch (error: any) {
      console.error("Error signing up:", error);
      setSignupError(
        error.response?.data?.message || "Signup failed. Please try again."
      );
    }
  };
  useEffect(() => {
    // An invite link is for a specific email, possibly not the one logged
    // in on this browser right now - bouncing into an unrelated existing
    // session here would silently sign the invited person in as whoever
    // else was already logged in, instead of letting them create their
    // own account.
    if (invitedEmail) return;

    const checkUserFromCookie = async () => {
      try {
        const authInfo = Cookies.get("authInfo");
        if (!authInfo) return; // No cookie found

        const { userId } = JSON.parse(authInfo);

        // Check if user ID exists
        if (userId) {
          const userData = await getUserById(userId);

          if (userData) {
            // User exists, redirect to homepage
            setUser(userData);
            navigate("/");
          }
        }
      } catch (error) {
        console.error("Error checking user from cookie:", error);
      }
    };
    // Check user from cookie
    checkUserFromCookie();
  }, [navigate, setUser, invitedEmail]);

  return (
    <Box sx={{ maxWidth: 400, margin: "auto", padding: 2 }}>
      <LogoContainer>
        <Logo />
        <Typography variant="h6" sx={{ color: "#000", fontWeight: "bold" }}>
          LetsLaunch
        </Typography>
      </LogoContainer>
      <Typography variant="h5">Sign Up</Typography>
      <br />

      {signupError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {signupError}
        </Alert>
      )}

      {/* Box for input fields */}
      <Box sx={{ marginBottom: 2 }}>
        <TextField
          required
          fullWidth
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          InputLabelProps={{
            shrink: true, // Keep the label always outside
            className: classes.inputLabel, // Use the custom label style
          }}
          className={classes.textField} // Apply margin style
        />
        <TextField
          required
          fullWidth
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={!!invitedEmail}
          helperText={invitedEmail ? "Pre-filled from your invitation" : undefined}
          InputLabelProps={{
            shrink: true, // Keep the label always outside
            className: classes.inputLabel, // Use the custom label style
          }}
          className={classes.textField} // Apply margin style
        />
        <TextField
          required
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          InputLabelProps={{
            shrink: true, // Keep the label always outside
            className: classes.inputLabel, // Use the custom label style
          }}
          className={classes.textField} // Apply margin style
        />
        <TextField
          required
          fullWidth
          label="Confirm Password" // New field for confirmation
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          InputLabelProps={{
            shrink: true, // Keep the label always outside
            className: classes.inputLabel, // Use the custom label style
          }}
          className={classes.textField} // Apply margin style
        />
        {/*<TextField
          required
          fullWidth
          label="Access Key"
          value={accessKey}
          type="password"
          onChange={(e) => setAccessKey(e.target.value)}
          InputLabelProps={{
            shrink: true, // Keep the label always outside
            className: classes.inputLabel, // Use the custom label style
          }}
          className={classes.textField} // Apply margin style
        />*/}
        {showOrganizationKeyField && (
          // Not needed for @spikeup.com emails - they verify by email instead
          // and get auto-assigned to the SpikeUp organization.
          <TextField
            required
            fullWidth
            label="Organization Key" // New field for organization key
            value={organizationKey}
            type="password"
            onChange={(e) => setOrganizationKey(e.target.value)}
            disabled={!!invitedOrgKey}
            helperText={invitedOrgKey ? "Pre-filled from your invitation" : undefined}
            InputLabelProps={{
              shrink: true, // Keep the label always outside
              className: classes.inputLabel, // Use the custom label style
            }}
            className={classes.textField} // Apply margin style
          />
        )}
        {passwordError && ( // Show error message if passwords don't match
          <Typography variant="body2" color="error" sx={{ marginTop: 1 }}>
            {passwordError}
          </Typography>
        )}
      </Box>

      {/* Box for buttons */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Button variant="contained" color="primary" onClick={handleSignup}>
          Sign Up
        </Button>
        <Button variant="text" href="/login">
          Already have an account? Login
        </Button>
      </Box>
    </Box>
  );
};

export default Signup;
